if (!customElements.get('rombo-localization')) {
  class RomboLocalization extends HTMLElement {
    static loadCountries() {
      if (!this.countriesPromise) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        this.countriesPromise = fetch('/api/2026-07/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            query: 'query RomboCountryLanguages { localization { availableCountries { isoCode defaultLanguage { isoCode } availableLanguages { isoCode endonymName } } } }',
          }),
        }).then(async (response) => {
          if (!response.ok) throw new Error('Localization unavailable');
          const result = await response.json();
          const countries = result.data?.localization?.availableCountries;
          if (result.errors?.length || !Array.isArray(countries)) throw new Error('Localization unavailable');
          return countries;
        }).catch((error) => {
          this.countriesPromise = null;
          throw error;
        }).finally(() => clearTimeout(timeout));
      }
      return this.countriesPromise;
    }

    connectedCallback() {
      this.details = this.querySelector('details');
      this.trigger = this.querySelector('summary');
      this.closeButton = this.querySelector('.rombo-localization__close');
      this.form = this.querySelector('form');
      if (!this.details || !this.trigger || !this.form || this.connected) return;

      this.connected = true;
      this.countryInput = this.form.querySelector('select[name="country_code"]');
      this.languageInput = this.form.querySelector('[name="language_code"]');
      this.saveButton = this.form.querySelector('[type="submit"]');
      this.languageError = this.form.querySelector('.rombo-localization__error');
      this.languageOptions = this.languageInput?.innerHTML;
      this.initialLanguage = this.languageInput?.value;
      this.languageUpdate = 0;
      this.desktop = window.innerWidth >= 1024;
      this.onToggle = () => {
        this.cancelLanguageUpdate();
        if (!this.details.open) return;
        if (this.languageInput?.tagName === 'SELECT') this.languageInput.innerHTML = this.languageOptions;
        this.form.reset();
        if (this.languageInput) this.languageInput.value = this.initialLanguage;
        if (this.countryInput && this.languageInput) RomboLocalization.loadCountries().catch(() => {});
        this.updatePanelHeight();
        document.querySelectorAll('rombo-localization').forEach((selector) => {
          if (selector !== this && typeof selector.close === 'function') selector.close();
        });
      };
      this.onOutside = (event) => {
        if (this.details.open && !this.contains(event.target)) this.close();
      };
      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      };
      this.onClose = () => this.close(true);
      this.onCountryChange = () => this.updateLanguage();
      this.onLanguageChange = () => {
        if (this.languageError) this.languageError.hidden = true;
      };
      this.onSubmit = (event) => {
        if (this.form.getAttribute('aria-busy') === 'true') event.preventDefault();
      };
      this.onResize = () => {
        const desktop = window.innerWidth >= 1024;
        if (desktop !== this.desktop) this.close();
        this.desktop = desktop;
        if (this.details.open) this.updatePanelHeight();
      };
      this.details.addEventListener('toggle', this.onToggle);
      this.addEventListener('keydown', this.onKeydown);
      this.countryInput?.addEventListener('change', this.onCountryChange);
      this.languageInput?.addEventListener('change', this.onLanguageChange);
      this.form.addEventListener('submit', this.onSubmit);
      document.addEventListener('click', this.onOutside);
      document.addEventListener('focusin', this.onOutside);
      window.addEventListener('resize', this.onResize);
      if (this.closeButton) {
        this.closeButton.hidden = false;
        this.closeButton.addEventListener('click', this.onClose);
      }
    }

    setLanguageBusy(busy) {
      this.form.setAttribute('aria-busy', String(busy));
      if (this.languageInput) this.languageInput.disabled = busy;
      if (this.saveButton) this.saveButton.disabled = busy;
    }

    cancelLanguageUpdate() {
      this.languageUpdate += 1;
      this.setLanguageBusy(false);
      if (this.languageError) this.languageError.hidden = true;
    }

    async updateLanguage() {
      if (!this.languageInput) return;
      const update = ++this.languageUpdate;
      const country = this.countryInput.value;
      this.setLanguageBusy(true);
      if (this.languageError) this.languageError.hidden = true;
      try {
        const countries = await RomboLocalization.loadCountries();
        if (update !== this.languageUpdate || !this.isConnected) return;
        const settings = countries.find((item) => item.isoCode === country);
        const language = settings?.defaultLanguage?.isoCode;
        if (!language || !settings.availableLanguages?.some((item) => item.isoCode === language)) {
          throw new Error('Country language unavailable');
        }
        const localeCode = (code) => code.toLowerCase().replace(/_/g, '-');
        if (this.languageInput.tagName === 'SELECT') {
          this.languageInput.replaceChildren(...settings.availableLanguages.map((item) => {
            const option = new Option(item.endonymName, localeCode(item.isoCode));
            option.lang = option.value;
            return option;
          }));
        }
        this.languageInput.value = localeCode(language);
        this.updatePanelHeight();
      } catch (error) {
        if (update !== this.languageUpdate || !this.isConnected) return;
        // Never silently keep an old language when the default couldn't be loaded.
        if (this.languageInput.tagName === 'SELECT') this.languageInput.selectedIndex = -1;
        if (this.languageError) this.languageError.hidden = false;
      } finally {
        if (update === this.languageUpdate) this.setLanguageBusy(false);
      }
    }

    updatePanelHeight() {
      const space = window.innerHeight - this.trigger.getBoundingClientRect().bottom - 24;
      this.style.setProperty('--rombo-localization-max-height', `${Math.max(120, space)}px`);
    }

    close(returnFocus = false) {
      if (!this.details || !this.details.open) return;
      this.cancelLanguageUpdate();
      this.details.open = false;
      if (returnFocus) this.trigger.focus();
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.details.removeEventListener('toggle', this.onToggle);
      this.removeEventListener('keydown', this.onKeydown);
      this.countryInput?.removeEventListener('change', this.onCountryChange);
      this.languageInput?.removeEventListener('change', this.onLanguageChange);
      this.form.removeEventListener('submit', this.onSubmit);
      this.cancelLanguageUpdate();
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('focusin', this.onOutside);
      window.removeEventListener('resize', this.onResize);
      if (this.closeButton) this.closeButton.removeEventListener('click', this.onClose);
      this.connected = false;
    }
  }

  customElements.define('rombo-localization', RomboLocalization);
}
